import React from 'react';
import AuthenticatedInput from '../AuthenticatedInput';
import { updateExamField } from '../../utils/examData';
import { useExamTab } from './ExamTabContext';
import JointReplacementPanel from './JointReplacementPanel';
import { TKA, replacementRating } from '../../utils/jointReplacement';
import {
  calculateKneeFlexion, calculateKneeFlexionContracture,
  calculateKneeVarus, calculateKneeValgus,
} from '../TabRatingNarratives';

const KneesTab: React.FC = () => {
  const {
    examData, setExamData, user, userProfile, startManualSession, setShowAuthModal,
    copyRegionValues, handleClearRegion, clearConfirmRegion, fillNormalValues,
  } = useExamTab();

  return (
                <div>
                  <h3 className="text-2xl font-semibold mb-6">Knees</h3>

                  <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6 items-start">

                  {/* Knee Rating Section — live WPI instrument (right column) */}
                  {examData && (
                    <div className="lg:order-2 lg:sticky lg:top-20">
                      <div className="card p-4 flex flex-col gap-4">
                        <div>
                          <span className="font-medium">Right Knee WPI:</span>
                          <span className="ml-2 text-xl font-bold">
                            {(() => {
                              // A replaced knee shows its TKA score, as the report does.
                              const tka = replacementRating(TKA, examData.knees, 'right');
                              if (tka) return `${tka.wpi}% (TKA)`;
                              // Calculate right knee impairment
                              const flexion = examData.knees.right_flexion;
                              const flexionContracture = examData.knees.right_flexion_contracture_passive;
                              const varus = examData.knees.right_varus;
                              const valgus = examData.knees.right_valgus;

                              // Check if right knee has any ROM values
                              if (flexion === null &&
                                  flexionContracture === null &&
                                  varus === null &&
                                  valgus === null) {
                                return "--";
                              }

                              // Sum the motions in % lower extremity, then convert LE -> WPI
                              const joint_sum = calculateKneeFlexion(flexion) +
                                                calculateKneeFlexionContracture(flexionContracture) +
                                                calculateKneeVarus(varus) +
                                                calculateKneeValgus(valgus);
                              const le_to_wpi = Math.round(joint_sum * 0.4);

                              return le_to_wpi + '%';
                            })()}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium">Left Knee WPI:</span>
                          <span className="ml-2 text-xl font-bold">
                            {(() => {
                              // A replaced knee shows its TKA score, as the report does.
                              const tka = replacementRating(TKA, examData.knees, 'left');
                              if (tka) return `${tka.wpi}% (TKA)`;
                              // Calculate left knee impairment
                              const flexion = examData.knees.left_flexion;
                              const flexionContracture = examData.knees.left_flexion_contracture_passive;
                              const varus = examData.knees.left_varus;
                              const valgus = examData.knees.left_valgus;

                              // Check if left knee has any ROM values
                              if (flexion === null &&
                                  flexionContracture === null &&
                                  varus === null &&
                                  valgus === null) {
                                return "--";
                              }

                              // Sum the motions in % lower extremity, then convert LE -> WPI
                              const joint_sum = calculateKneeFlexion(flexion) +
                                                calculateKneeFlexionContracture(flexionContracture) +
                                                calculateKneeVarus(varus) +
                                                calculateKneeValgus(valgus);
                              const le_to_wpi = Math.round(joint_sum * 0.4);

                              return le_to_wpi + '%';
                            })()}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Range of Motion Section — left column */}
                  <div className="lg:order-1 mb-0">
                    <div className="flex justify-between items-center mb-4 border-b border-border pb-2">
                      <h4 className="text-lg font-medium">Range of Motion</h4>
                      <div className="flex gap-2 flex-wrap"><button onClick={() => copyRegionValues('knees', 'right_to_left')} className="btn-secondary text-xs" title="Copy Right values to Left">R → L</button><button onClick={() => copyRegionValues('knees', 'left_to_right')} className="btn-secondary text-xs" title="Copy Left values to Right">L → R</button><button onClick={() => handleClearRegion('knees')} className={clearConfirmRegion === 'knees' ? 'btn-danger text-xs' : 'btn-secondary text-xs'}>{clearConfirmRegion === 'knees' ? 'Confirm Clear?' : 'Clear Values'}</button><button onClick={() => fillNormalValues('knees')} className="btn-secondary text-xs" disabled={!user}>Fill Normal Values</button></div>
                    </div>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-x-4 gap-y-4">
                      {/* Header Row */}
                      <div className="col-span-1"></div>
                      <div className="col-span-1 text-center text-xs font-medium text-content-tertiary hidden sm:block">Normal</div>
                      <div className="col-span-1 text-center font-medium">Right</div>
                      <div className="col-span-1 text-center font-medium">Left</div>

                      {/* Flexion Row */}
                      <div className="col-span-1 text-content-secondary">Flexion</div>
                      <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">150°</div>
                      <div className="col-span-1">
                        <AuthenticatedInput type="number" className="input-field text-center" value={examData?.knees?.right_flexion ?? ''} onChange={(value) => { const numValue = value === '' || value === null ? null : Number(value); setExamData(prev => prev ? { ...prev, knees: { ...prev.knees, right_flexion: numValue } } : null); }} user={user} userProfile={userProfile} onSessionStart={startManualSession} onShowAuthModal={() => setShowAuthModal(true)} />
                      </div>
                      <div className="col-span-1">
                        <AuthenticatedInput type="number" className="input-field text-center" value={examData?.knees?.left_flexion ?? ''} onChange={(value) => { const numValue = value === '' || value === null ? null : Number(value); setExamData(prev => prev ? { ...prev, knees: { ...prev.knees, left_flexion: numValue } } : null); }} user={user} userProfile={userProfile} onSessionStart={startManualSession} onShowAuthModal={() => setShowAuthModal(true)} />
                      </div>

                      {/* No separate "Extension" row: it duplicated Flexion Contracture (rated)
                          and Extensor Lag (not rated), was never rated itself, and the parser
                          never fills it, so a value typed there was silently ignored. */}

                      {/* Flexion Contracture (Passive) Row */}
                      <div className="col-span-1 text-content-secondary">Flexion Contracture</div>
                      <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">0°</div>
                      <div className="col-span-1">
                        <AuthenticatedInput
                          type="number"
                          className="input-field text-center"
                          value={examData?.knees?.right_flexion_contracture_passive ?? ''}
                          onChange={(value) => {
                            const numValue = value === '' || value === null ? null : Number(value);
                            setExamData(prev => prev ? {
                              ...prev,
                              knees: {
                                ...prev.knees,
                                right_flexion_contracture_passive: numValue
                              }
                            } : null);
                          }}
                          user={user}
                          userProfile={userProfile}
                          onSessionStart={startManualSession}
                          onShowAuthModal={() => setShowAuthModal(true)}
                        />
                      </div>
                      <div className="col-span-1">
                        <AuthenticatedInput
                          type="number"
                          className="input-field text-center"
                          value={examData?.knees?.left_flexion_contracture_passive ?? ''}
                          onChange={(value) => {
                            const numValue = value === '' || value === null ? null : Number(value);
                            setExamData(prev => prev ? {
                              ...prev,
                              knees: {
                                ...prev.knees,
                                left_flexion_contracture_passive: numValue
                              }
                            } : null);
                          }}
                          user={user}
                          userProfile={userProfile}
                          onSessionStart={startManualSession}
                          onShowAuthModal={() => setShowAuthModal(true)}
                        />
                      </div>
                      
                      {/* Extensor Lag Row */}
                      <div className="col-span-1 text-content-secondary">Extensor Lag</div>
                      <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">0°</div>
                      <div className="col-span-1">
                        <AuthenticatedInput
                          type="number"
                          className="input-field text-center"
                          value={examData?.knees?.right_extensor_lag ?? ''}
                          onChange={(value) => {
                            const numValue = value === '' || value === null ? null : Number(value);
                            setExamData(prev => prev ? {
                              ...prev,
                              knees: {
                                ...prev.knees,
                                right_extensor_lag: numValue
                              }
                            } : null);
                          }}
                          user={user}
                          userProfile={userProfile}
                          onSessionStart={startManualSession}
                          onShowAuthModal={() => setShowAuthModal(true)}
                        />
                      </div>
                      <div className="col-span-1">
                        <AuthenticatedInput
                          type="number"
                          className="input-field text-center"
                          value={examData?.knees?.left_extensor_lag ?? ''}
                          onChange={(value) => {
                            const numValue = value === '' || value === null ? null : Number(value);
                            setExamData(prev => prev ? {
                              ...prev,
                              knees: {
                                ...prev.knees,
                                left_extensor_lag: numValue
                              }
                            } : null);
                          }}
                          user={user}
                          userProfile={userProfile}
                          onSessionStart={startManualSession}
                          onShowAuthModal={() => setShowAuthModal(true)}
                        />
                      </div>
                      
                      {/* Varus Row */}
                      <div className="col-span-1 text-content-secondary">Varus</div>
                      <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">0°</div>
                      <div className="col-span-1">
                        <AuthenticatedInput
                          type="number"
                          className="input-field text-center"
                          value={examData?.knees?.right_varus ?? ''}
                          onChange={(value) => {
                            const numValue = value === '' || value === null ? null : Number(value);
                            setExamData(prev => prev ? {
                              ...prev,
                              knees: {
                                ...prev.knees,
                                right_varus: numValue
                              }
                            } : null);
                          }}
                          user={user}
                          userProfile={userProfile}
                          onSessionStart={startManualSession}
                          onShowAuthModal={() => setShowAuthModal(true)}
                        />
                      </div>
                      <div className="col-span-1">
                        <AuthenticatedInput
                          type="number"
                          className="input-field text-center"
                          value={examData?.knees?.left_varus ?? ''}
                          onChange={(value) => {
                            const numValue = value === '' || value === null ? null : Number(value);
                            setExamData(prev => prev ? {
                              ...prev,
                              knees: {
                                ...prev.knees,
                                left_varus: numValue
                              }
                            } : null);
                          }}
                          user={user}
                          userProfile={userProfile}
                          onSessionStart={startManualSession}
                          onShowAuthModal={() => setShowAuthModal(true)}
                        />
                      </div>
                      
                      {/* Valgus Row */}
                      <div className="col-span-1 text-content-secondary">Valgus</div>
                      <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">0°</div>
                      <div className="col-span-1">
                        <AuthenticatedInput
                          type="number"
                          className="input-field text-center"
                          value={examData?.knees?.right_valgus ?? ''}
                          onChange={(value) => {
                            const numValue = value === '' || value === null ? null : Number(value);
                            setExamData(prev => prev ? {
                              ...prev,
                              knees: {
                                ...prev.knees,
                                right_valgus: numValue
                              }
                            } : null);
                          }}
                          user={user}
                          userProfile={userProfile}
                          onSessionStart={startManualSession}
                          onShowAuthModal={() => setShowAuthModal(true)}
                        />
                      </div>
                      <div className="col-span-1">
                        <AuthenticatedInput
                          type="number"
                          className="input-field text-center"
                          value={examData?.knees?.left_valgus ?? ''}
                          onChange={(value) => {
                            const numValue = value === '' || value === null ? null : Number(value);
                            setExamData(prev => prev ? {
                              ...prev,
                              knees: {
                                ...prev.knees,
                                left_valgus: numValue
                              }
                            } : null);
                          }}
                          user={user}
                          userProfile={userProfile}
                          onSessionStart={startManualSession}
                          onShowAuthModal={() => setShowAuthModal(true)}
                        />
                      </div>
                    </div>
                  </div>
                  </div>

                  <JointReplacementPanel config={TKA} />
                  
                </div>
  );
};

export default KneesTab;
